import { Injectable, computed, inject, signal } from '@angular/core';
import { Store } from '@ngxs/store';

import { CatchesService } from './catches.service';
import { AuthState } from '@state/auth/auth.state';

/**
 * Manages the background download of a user's existing catches from
 * the server after login. Exposes reactive signals so any page (in
 * particular My Catches) can render a progress indicator.
 *
 * Lifecycle:
 *   - LoginPage calls start() after a successful login, just before
 *     navigating to /tabs. We don't await it — the user is redirected
 *     immediately and catches stream into IndexedDB in the background.
 *   - Pages observe active/done/total via signals and render progress
 *     UI. lastResult is set when the download finishes (success or
 *     failure) so the UI can show a final "downloaded N catches" toast.
 *
 * One download at a time. If start() is called while another is in
 * progress, the second call no-ops — a re-login while the previous
 * download is still running shouldn't kick off a parallel one.
 */
@Injectable({ providedIn: 'root' })
export class CatchDownloadService {
  private readonly catches = inject(CatchesService);
  private readonly store = inject(Store);

  /** True while a download is in progress. */
  readonly active = signal<boolean>(false);
  /** Number of catches processed so far. */
  readonly done = signal<number>(0);
  /** Total number of catches the server reports. 0 if not yet known. */
  readonly total = signal<number>(0);

  /**
   * Result of the most recent download, available after active flips
   * back to false. Null if no download has run yet (or if it's still
   * running). Useful for one-shot UI like "Synced 12 catches" toasts.
   */
  readonly lastResult = signal<{
    added: number;
    skipped: number;
    total: number;
    error?: string;
  } | null>(null);

  /**
   * 0..1 progress ratio for progress-bar UIs. Returns 0 when total
   * is unknown (avoids NaN/Infinity from division by zero).
   */
  readonly progressRatio = computed<number>(() => {
    const t = this.total();
    if (t <= 0) return 0;
    return Math.min(1, this.done() / t);
  });

  /** Last time quickSyncIfStale ran (epoch ms). 0 = never. */
  private lastQuickCheckAt = 0;

  /** Cooldown in ms between successive quickSyncIfStale runs.
   *  Long enough that tab-flipping doesn't generate request storms,
   *  short enough that a real device-switching scenario gets picked
   *  up within a minute. */
  private readonly QUICK_CHECK_COOLDOWN_MS = 60_000;

  /**
   * Kicks off a fresh download. No-ops if one is already running.
   * Resolves when the download finishes (caller can choose to await
   * or ignore — the LoginPage ignores so navigation isn't blocked).
   */
  async start(): Promise<void> {
    if (this.active()) return;

    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId === null) return;

    this.active.set(true);
    this.done.set(0);
    this.total.set(0);
    this.lastResult.set(null);

    try {
      const result = await this.catches.downloadFromServer(
        userId,
        (done, total) => {
          this.done.set(done);
          this.total.set(total);
        },
      );
      this.lastResult.set(result);
    } catch (err) {
      // Surface the error in lastResult so the UI can decide how to
      // tell the user. A failed download isn't fatal — the user can
      // still use the app with their local-only state, and a retry
      // would happen naturally at next login.
      this.lastResult.set({
        added: 0,
        skipped: 0,
        total: 0,
        error: err instanceof Error ? err.message : 'Download failed.',
      });
    } finally {
      this.active.set(false);
    }
  }

  /**
   * Lightweight "are we stale?" check. Compares local catch count
   * to the server's count. If the server has more, kicks off a full
   * download (which will skip everything we already have, so only
   * the new catches actually round-trip). If counts match or the
   * server has fewer (deletion happened elsewhere), no-ops — we
   * can't reliably reconcile deletions with the iOS API shape, so
   * we don't try.
   *
   * Throttled by QUICK_CHECK_COOLDOWN_MS so rapid focus events
   * (tab-switching during testing, or coming back to the PWA every
   * few seconds) don't generate unnecessary requests. Pass
   * `force=true` from manual user actions to bypass the cooldown.
   */
  async quickSyncIfStale(force = false): Promise<void> {
    if (this.active()) return;

    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId === null) return;

    const now = Date.now();
    if (!force && now - this.lastQuickCheckAt < this.QUICK_CHECK_COOLDOWN_MS) {
      return;
    }
    this.lastQuickCheckAt = now;

    const remoteCount = await this.catches.countServerCatches(userId);
    if (remoteCount === null) return; // Network failed; just bail.

    const localCount = await this.catches.countLocalCatches(userId);
    if (remoteCount > localCount) {
      // Server has catches we don't. Kick off a full download —
      // downloadFromServer's skip-if-exists logic means we'll only
      // actually round-trip and persist the new ones.
      this.start();
    }
    // else: counts match, or remote < local. Either way, nothing
    // new to fetch. We deliberately don't try to detect remote
    // deletions here; that's a known gap (see service header).
  }
}
